import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import PhaseBadge from '../components/PhaseBadge';
import { getEditPassword, authHeaders } from '../../services/auth';
import PasswordGate from '../../components/PasswordGate';

const PAGE_SIZE = 20;

function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [company,  setCompany]  = useState(null);
  const [programs, setPrograms] = useState([]);
  const [trials,   setTrials]   = useState([]);
  const [filings,  setFilings]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const [activeTab,       setActiveTab]       = useState('pipeline');
  const [trialsPage,      setTrialsPage]      = useState(1);
  const [filingsPage,     setFilingsPage]     = useState(1);
  const [editMode,        setEditMode]        = useState(() => !!getEditPassword());
  const [showUnlockGate,  setShowUnlockGate]  = useState(false);
  const [deleting,        setDeleting]        = useState(false);
  const [pipelineUrls,    setPipelineUrls]    = useState([]);
  const [addingUrl,       setAddingUrl]       = useState(false);
  const [newUrl,          setNewUrl]          = useState({ url: '', label: '' });
  const [savingUrl,       setSavingUrl]       = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_BASE_URL}/companies/${id}`),
      axios.get(`${API_BASE_URL}/programs`, { params: { company_id: id, status: 'all' } }),
      axios.get(`${API_BASE_URL}/companies/${id}/trials`),
      axios.get(`${API_BASE_URL}/companies/${id}/filings`),
      axios.get(`${API_BASE_URL}/pipeline/urls`),
    ])
      .then(([compRes, progRes, trialsRes, filingsRes, urlsRes]) => {
        setCompany(compRes.data);
        const progData = progRes.data;
        setPrograms(Array.isArray(progData) ? progData : (progData.programs || []));
        setTrials(trialsRes.data?.trials || trialsRes.data || []);
        setFilings(filingsRes.data?.filings || filingsRes.data || []);
        setPipelineUrls((urlsRes.data || []).filter(u => String(u.company_id) === String(id)));
      })
      .catch(() => setError('Failed to load company details.'))
      .finally(() => setLoading(false));
  }, [id]);

  async function handleAddUrl(e) {
    e.preventDefault();
    if (!newUrl.url.trim()) return;
    setSavingUrl(true);
    try {
      await axios.post(`${API_BASE_URL}/pipeline/urls`, {
        company_id: Number(id), url: newUrl.url.trim(), label: newUrl.label.trim() || undefined,
      }, { headers: authHeaders() });
      const res = await axios.get(`${API_BASE_URL}/pipeline/urls`);
      setPipelineUrls((res.data || []).filter(u => String(u.company_id) === String(id)));
      setNewUrl({ url: '', label: '' });
      setAddingUrl(false);
    } finally {
      setSavingUrl(false);
    }
  }

  async function handleRemoveUrl(urlId) {
    if (!window.confirm('Remove this pipeline URL?')) return;
    await axios.delete(`${API_BASE_URL}/pipeline/urls/${urlId}`, { headers: authHeaders() });
    setPipelineUrls(prev => prev.filter(u => u.id !== urlId));
  }

  if (loading) return <div className="loading-state">Loading company…</div>;
  if (error)   return <div className="error-state">{error}</div>;
  if (!company) return null;

  const trialsTotalPages  = Math.max(1, Math.ceil(trials.length / PAGE_SIZE));
  const trialsSlice       = trials.slice((trialsPage - 1) * PAGE_SIZE, trialsPage * PAGE_SIZE);

  const filingsTotalPages = Math.max(1, Math.ceil(filings.length / PAGE_SIZE));
  const filingsSlice      = filings.slice((filingsPage - 1) * PAGE_SIZE, filingsPage * PAGE_SIZE);

  async function handleDelete() {
    const warning = programs.length > 0
      ? `This company has ${programs.length} program(s). Deleting it will also delete all associated programs, trials, and changes. Are you sure?`
      : 'Delete this company? This cannot be undone.';
    if (!window.confirm(warning)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/companies/${id}`, { headers: authHeaders() });
      navigate('/pipeline/companies');
    } catch {
      alert('Failed to delete company.');
      setDeleting(false);
    }
  }

  return (
    <div>
      <div className="page-header">
        <Link to="/pipeline/companies" className="back-link">&larr; Back to Companies</Link>
        <h1 className="page-title">{company.name}</h1>
        <p className="page-subtitle">
          {[company.country, company.company_type].filter(Boolean).join(' · ')}
        </p>
        {editMode ? (
          <button
            onClick={handleDelete}
            disabled={deleting}
            style={{ marginTop: 6, fontSize: 12, padding: '4px 14px', border: '1px solid #fca5a5', borderRadius: 5, background: '#fff', color: '#dc2626', cursor: 'pointer' }}
          >
            {deleting ? 'Deleting…' : 'Delete company'}
          </button>
        ) : showUnlockGate ? (
          <PasswordGate onUnlocked={() => { setEditMode(true); setShowUnlockGate(false); }} />
        ) : (
          <button className="btn-secondary" style={{ marginTop: 4 }} onClick={() => setShowUnlockGate(true)}>
            Edit
          </button>
        )}
      </div>

      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '12px 28px', display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'center' }}>
        <StatChip label="Programs" value={programs.length} />
        <StatChip label="Trials"   value={trials.length} />
        <StatChip label="Filings"  value={filings.length} />
        {company.ticker && <StatChip label="Ticker" value={company.ticker} />}
        <a href={`${API_BASE_URL}/pipeline/export.csv?company_id=${company.id}`} className="ext-link" style={{ alignSelf: 'center' }}
           title="Programs compared against this company's pipeline page (checked weekly)">
          Export CSV &darr;
        </a>
        {company.website && (
          <a href={company.website} target="_blank" rel="noopener noreferrer" className="ext-link" style={{ alignSelf: 'center' }}>
            Website &rarr;
          </a>
        )}
        {pipelineUrls.map(u => (
          <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <a href={u.url} target="_blank" rel="noopener noreferrer" className="ext-link" style={{ alignSelf: 'center' }}>
              {u.label || 'Pipeline Page'} &rarr;
            </a>
            {editMode && (
              <button
                onClick={() => handleRemoveUrl(u.id)}
                style={{ fontSize: 10, padding: '1px 5px', border: '1px solid #fca5a5', borderRadius: 3, background: '#fff', color: '#dc2626', cursor: 'pointer' }}
              >
                ✕
              </button>
            )}
          </div>
        ))}
        {editMode && !addingUrl && (
          <button
            onClick={() => setAddingUrl(true)}
            style={{ fontSize: 12, padding: '3px 10px', border: '1px solid #d1d5db', borderRadius: 5, background: '#f8fafc', cursor: 'pointer', color: '#475569', alignSelf: 'center' }}
          >
            + Pipeline URL
          </button>
        )}
        {editMode && addingUrl && (
          <form onSubmit={handleAddUrl} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="url"
              placeholder="https://…"
              value={newUrl.url}
              onChange={e => setNewUrl(u => ({ ...u, url: e.target.value }))}
              style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 5, width: 220 }}
            />
            <input
              type="text"
              placeholder="Label (optional)"
              value={newUrl.label}
              onChange={e => setNewUrl(u => ({ ...u, label: e.target.value }))}
              style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 5, width: 140 }}
            />
            <button type="submit" disabled={savingUrl} className="btn-primary" style={{ fontSize: 12, padding: '4px 12px' }}>
              {savingUrl ? 'Saving…' : 'Add'}
            </button>
            <button type="button" onClick={() => setAddingUrl(false)} className="btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }}>
              Cancel
            </button>
          </form>
        )}
      </div>

      <div className="tab-bar">
        {['pipeline', 'trials', 'filings'].map(tab => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            <span style={{ marginLeft: 6, fontSize: 11, color: '#94a3b8' }}>
              {tab === 'pipeline' ? programs.length
               : tab === 'trials' ? trials.length
               : filings.length}
            </span>
          </button>
        ))}
      </div>

      <div className="page-body">

        {activeTab === 'pipeline' && (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Drug Name</th>
                  <th>Brand Name</th>
                  <th>Indication</th>
                  <th>Phase</th>
                  <th>Modality</th>
                  <th>Target</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {programs.length === 0 ? (
                  <tr><td colSpan={7} className="table-empty">No pipeline programs found.</td></tr>
                ) : programs.map(p => (
                  <tr key={p.id} className="clickable-row" onClick={() => navigate(`/pipeline/programs/${p.id}`)}>
                    <td><strong>{p.drug_name || '—'}</strong></td>
                    <td>{p.brand_name || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                    <td>{p.indication_name || p.indication?.name || '—'}</td>
                    <td><PhaseBadge phase={p.current_phase} /></td>
                    <td>{p.modality || '—'}</td>
                    <td>{p.target   || '—'}</td>
                    <td>{p.status   || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'trials' && (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>NCT ID</th>
                  <th>Title</th>
                  <th>Phase</th>
                  <th>Status</th>
                  <th>Start Date</th>
                </tr>
              </thead>
              <tbody>
                {trialsSlice.length === 0 ? (
                  <tr><td colSpan={5} className="table-empty">No clinical trials found.</td></tr>
                ) : trialsSlice.map((t, i) => (
                  <tr key={t.id || t.nct_id || i}>
                    <td>
                      {t.nct_id ? (
                        <a
                          href={`https://clinicaltrials.gov/study/${t.nct_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ext-link"
                          onClick={e => e.stopPropagation()}
                        >
                          {t.nct_id}
                        </a>
                      ) : '—'}
                    </td>
                    <td style={{ maxWidth: 400 }}>{t.title || t.brief_title || '—'}</td>
                    <td><PhaseBadge phase={t.phase} /></td>
                    <td>{t.status || t.overall_status || '—'}</td>
                    <td>{t.start_date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {trials.length > PAGE_SIZE && (
              <div className="pagination">
                <span className="pagination-info">
                  Page {trialsPage} of {trialsTotalPages}
                </span>
                <button
                  className="pagination-btn"
                  disabled={trialsPage === 1}
                  onClick={() => setTrialsPage(p => p - 1)}
                >
                  Previous
                </button>
                <button
                  className="pagination-btn"
                  disabled={trialsPage === trialsTotalPages}
                  onClick={() => setTrialsPage(p => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'filings' && (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Form Type</th>
                  <th>Filed Date</th>
                  <th>Description</th>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                {filingsSlice.length === 0 ? (
                  <tr><td colSpan={4} className="table-empty">No EDGAR filings found.</td></tr>
                ) : filingsSlice.map((f, i) => (
                  <tr key={f.id || i}>
                    <td><strong>{f.filing_type || f.form_type || '—'}</strong></td>
                    <td>{f.filed_date || f.filing_date || '—'}</td>
                    <td style={{ maxWidth: 360 }}>{f.description || f.title || '—'}</td>
                    <td>
                      {(f.form_url || f.url || f.filing_url) ? (
                        <a
                          href={f.form_url || f.url || f.filing_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ext-link"
                        >
                          View filing &rarr;
                        </a>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filings.length > PAGE_SIZE && (
              <div className="pagination">
                <span className="pagination-info">
                  Page {filingsPage} of {filingsTotalPages}
                </span>
                <button
                  className="pagination-btn"
                  disabled={filingsPage === 1}
                  onClick={() => setFilingsPage(p => p - 1)}
                >
                  Previous
                </button>
                <button
                  className="pagination-btn"
                  disabled={filingsPage === filingsTotalPages}
                  onClick={() => setFilingsPage(p => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatChip({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <span style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>{value}</span>
      <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8' }}>{label}</span>
    </div>
  );
}

export default CompanyDetail;
